import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  SimpleGrid,
  Button,
  Input,
  Textarea,
  Spinner,
  Badge,
} from '@chakra-ui/react';
import apiService from '../services/api';
import SlotCalendar from '../components/SlotCalendar';

const ET_TZ = 'America/New_York';

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: ET_TZ,
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ET_TZ,
  });

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Scheduling = () => {
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [selectedType, setSelectedType] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '', topic: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmed, setConfirmed] = useState(null);

  const formRef = useRef(null);

  // When a time is picked, the details form appears below — scroll to it so
  // it's clear that filling out the form is the next step (esp. on mobile).
  useEffect(() => {
    if (selectedSlot && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSlot]);

  // Load meeting types once.
  useEffect(() => {
    let active = true;
    apiService.scheduling
      .getMeetingTypes()
      .then((res) => {
        if (!active) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setMeetingTypes(data);
      })
      .catch(() => active && setMeetingTypes([]))
      .finally(() => active && setLoadingTypes(false));
    return () => {
      active = false;
    };
  }, []);

  // Load slots whenever a meeting type is chosen.
  useEffect(() => {
    if (!selectedType) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    apiService.scheduling
      .getSlots(selectedType.id)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setSlots(data);
      })
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.full_name.trim()) errors.full_name = 'Name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!validateEmail(form.email)) errors.email = 'Enter a valid email address';
    if (!selectedSlot) errors.slot = 'Please pick a time';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await apiService.scheduling.book({
        ...form,
        slot_id: selectedSlot.id,
        meeting_type_id: selectedType.id,
      });
      setConfirmed({ slot: selectedSlot, type: selectedType, name: form.full_name });
    } catch (err) {
      const detail =
        err.response?.data?.slot_id?.[0] ||
        err.response?.data?.detail ||
        'Something went wrong booking your meeting. Please try again.';
      setSubmitError(detail);
      // A taken slot means our list is stale — refresh it.
      if (selectedType) {
        apiService.scheduling
          .getSlots(selectedType.id)
          .then((res) => setSlots(Array.isArray(res.data) ? res.data : res.data?.results || []))
          .catch(() => {});
        setSelectedSlot(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────
  if (confirmed) {
    return (
      <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
        <VStack spacing={8} maxW="2xl" mx="auto" textAlign="center">
          <Box w={16} h={16} borderRadius="full" bg="green.500" display="flex" alignItems="center" justifyContent="center" fontSize="3xl">
            ☘
          </Box>
          <Heading size={{ base: 'lg', md: 'xl' }} textTransform="uppercase" letterSpacing="wide">
            You're booked, {confirmed.name.split(' ')[0]}.
          </Heading>
          <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" p={8} w="100%">
            <Text color="green.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide" mb={1}>
              When
            </Text>
            <Text color="white" fontSize="xl" fontWeight="bold">
              {fmtDay(confirmed.slot.start_time)}
            </Text>
            <Text color="white" fontSize="lg">
              {fmtTime(confirmed.slot.start_time)} ET
            </Text>
            <Text color="green.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide" mt={4} mb={1}>
              Meeting
            </Text>
            <Text color="gray.200">{confirmed.type.name}</Text>
          </Box>
          <Text color="gray.300" lineHeight="1.8">
            A confirmation email is on its way{' '}
            {confirmed.type.default_mode === 'virtual' && 'with your Google Meet link'}. We look forward to
            speaking with you.
          </Text>
          <Button as="a" href="/" bg="green.500" color="white" _hover={{ bg: 'green.600' }}>
            Back to home
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
      <VStack spacing={{ base: 12, md: 16 }} maxW="4xl" mx="auto">
        {/* Hero */}
        <VStack spacing={{ base: 6, md: 8 }} textAlign="center">
          <Heading size={{ base: 'xl', md: '2xl', lg: '4xl' }} textTransform="uppercase" letterSpacing="wide">
            Schedule a Meeting
          </Heading>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.300" maxW="2xl" lineHeight="1.8">
            Pick a meeting type and a time that works for you. All times shown in Eastern Time (ET).
          </Text>
        </VStack>

        {/* Step 1: meeting type */}
        <Box w="100%">
          <HStack mb={6}>
            <Box w={3} h={3} bg="green.500" borderRadius="full" />
            <Heading size={{ base: 'md', md: 'lg' }} textTransform="uppercase" letterSpacing="wide">
              1. Choose a meeting type
            </Heading>
          </HStack>
          {loadingTypes ? (
            <HStack justify="center" py={8}>
              <Spinner color="green.500" />
            </HStack>
          ) : meetingTypes.length === 0 ? (
            <Text color="gray.400">No meeting types are available right now. Please check back soon.</Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {meetingTypes.map((mt) => {
                const active = selectedType?.id === mt.id;
                return (
                  <Box
                    key={mt.id}
                    as="button"
                    type="button"
                    onClick={() => setSelectedType(mt)}
                    textAlign="left"
                    bg={active ? 'green.900' : 'gray.900'}
                    border="1px solid"
                    borderColor={active ? 'green.500' : 'gray.700'}
                    borderRadius="md"
                    p={6}
                    transition="all 0.2s"
                    _hover={{ borderColor: 'green.500' }}
                  >
                    <HStack justify="space-between" mb={2}>
                      <Heading size="sm" color="white">
                        {mt.name}
                      </Heading>
                      <Badge bg="gray.700" color="green.300">
                        {mt.duration_minutes} min
                      </Badge>
                    </HStack>
                    {mt.description && (
                      <Text color="gray.400" fontSize="sm" lineHeight="1.6">
                        {mt.description}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Box>

        {/* Step 2: time slot */}
        {selectedType && (
          <Box w="100%">
            <HStack mb={6}>
              <Box w={3} h={3} bg="green.500" borderRadius="full" />
              <Heading size={{ base: 'md', md: 'lg' }} textTransform="uppercase" letterSpacing="wide">
                2. Pick a time
              </Heading>
            </HStack>
            {loadingSlots ? (
              <HStack justify="center" py={8}>
                <Spinner color="green.500" />
              </HStack>
            ) : slots.length === 0 ? (
              <Text color="gray.400">
                No open times right now. Reach out via the{' '}
                <Box as="a" href="/contact" color="green.400" textDecoration="underline">
                  contact page
                </Box>{' '}
                and we'll find a time.
              </Text>
            ) : (
              <SlotCalendar
                slots={slots}
                selectedSlot={selectedSlot}
                onSelect={(slot) => {
                  setSelectedSlot(slot);
                  setFieldErrors((prev) => ({ ...prev, slot: '' }));
                }}
              />
            )}
            {fieldErrors.slot && (
              <Text color="red.300" fontSize="sm" mt={3}>
                {fieldErrors.slot}
              </Text>
            )}
          </Box>
        )}

        {/* Step 3: details */}
        {selectedType && selectedSlot && (
          <Box ref={formRef} scrollMarginTop={{ base: '80px', md: '100px' }} w="100%" bg="gray.900" border="1px solid" borderColor="green.600" borderRadius="md" p={{ base: 6, md: 10 }}>
            <HStack mb={2}>
              <Box w={3} h={3} bg="green.500" borderRadius="full" />
              <Heading size={{ base: 'md', md: 'lg' }} textTransform="uppercase" letterSpacing="wide">
                3. Your details
              </Heading>
            </HStack>
            <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }} mb={6}>
              Last step — tell us who you are and we'll lock in your time.
            </Text>
            <Box as="form" onSubmit={handleSubmit}>
              <VStack spacing={6}>
                {[
                  { name: 'full_name', label: 'Full Name *', placeholder: 'Your full name' },
                  { name: 'email', label: 'Email Address *', placeholder: 'your@email.com', type: 'email' },
                  { name: 'phone', label: 'Phone', placeholder: '(optional)' },
                  { name: 'company', label: 'Company', placeholder: '(optional)' },
                ].map((f) => (
                  <VStack key={f.name} spacing={2} align="start" w="100%">
                    <Text color="white" fontSize="sm" textTransform="uppercase" letterSpacing="wide" fontWeight="bold">
                      {f.label}
                    </Text>
                    <Input
                      name={f.name}
                      type={f.type || 'text'}
                      value={form[f.name]}
                      onChange={handleChange}
                      bg="gray.800"
                      border="1px solid"
                      borderColor={fieldErrors[f.name] ? 'red.500' : 'gray.600'}
                      color="white"
                      _hover={{ borderColor: 'gray.500' }}
                      _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                      placeholder={f.placeholder}
                      _placeholder={{ color: 'gray.500' }}
                    />
                    {fieldErrors[f.name] && (
                      <Text color="red.300" fontSize="sm">
                        {fieldErrors[f.name]}
                      </Text>
                    )}
                  </VStack>
                ))}
                <VStack spacing={2} align="start" w="100%">
                  <Text color="white" fontSize="sm" textTransform="uppercase" letterSpacing="wide" fontWeight="bold">
                    What would you like to discuss?
                  </Text>
                  <Textarea
                    name="topic"
                    value={form.topic}
                    onChange={handleChange}
                    bg="gray.800"
                    border="1px solid"
                    borderColor="gray.600"
                    color="white"
                    rows={4}
                    _hover={{ borderColor: 'gray.500' }}
                    _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                    placeholder="A short note on what you'd like to cover (optional)"
                    _placeholder={{ color: 'gray.500' }}
                  />
                </VStack>

                <Box w="100%" bg="black" border="1px solid" borderColor="gray.700" borderRadius="md" p={4}>
                  <Text color="gray.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                    Booking
                  </Text>
                  <Text color="white" fontWeight="bold">
                    {selectedType.name} · {fmtDay(selectedSlot.start_time)} at {fmtTime(selectedSlot.start_time)} ET
                  </Text>
                </Box>

                {submitError && (
                  <Text color="red.300" fontSize="sm" w="100%">
                    {submitError}
                  </Text>
                )}

                <Button
                  type="submit"
                  isLoading={submitting}
                  loadingText="Booking..."
                  bg="green.500"
                  color="white"
                  size="lg"
                  w="100%"
                  _hover={{ bg: 'green.600' }}
                >
                  Confirm booking
                </Button>
              </VStack>
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default Scheduling;
